import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Home from "@/pages/Home";
import Search from "@/pages/Search";
import DoctorProfile from "@/pages/DoctorProfile";
import DoctorAuth from "@/pages/DoctorAuth";
import DoctorDashboard from "@/pages/DoctorDashboard";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentResult from "@/pages/PaymentResult";
import ManageBooking from "@/pages/ManageBooking";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import PatientLogin from "@/pages/PatientLogin";
import PatientVerify from "@/pages/PatientVerify";
import PatientDashboard from "@/pages/PatientDashboard";
import ReviewPage from "@/pages/ReviewPage";
import ConfirmBooking from "@/pages/ConfirmBooking";
import EmbedBooking from "@/pages/EmbedBooking";
import PartnerRegister from "@/pages/PartnerRegister";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerReset from "@/pages/PartnerReset";
import DoctorReset from "@/pages/DoctorReset";
import PartnerDashboard from "@/pages/PartnerDashboard";
import AboutUs from "@/pages/AboutUs";
import { ParaMedicosLanding, ParaMedicosCity, MedicosCity } from "@/pages/CityPages";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import PwaBanner from "@/components/PwaBanner";
import MetaPixel from "@/components/MetaPixel";

function RequireRole({ role, children }) {
  const { role: currentRole, token } = useApp();
  const loc = useLocation();
  if (!token || currentRole !== role) {
    // Each protected zone bounces to its OWN login, preserving the originally
    // requested path so a successful sign-in returns the user to that exact
    // page (fixes /socio/panel landing on the doctor login).
    const loginPath =
      role === "admin"   ? "/admin/login"   :
      role === "partner" ? "/socio/login"   :
      role === "patient" ? "/paciente/login":
                           "/doctor/login";
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`${loginPath}?next=${next}`} replace />;
  }
  return children;
}

/** Visit tracker — fires one POST /api/visits per route change on PUBLIC pages
 *  only. Private spaces (/doctor, /admin, /paciente, /socio) are skipped so
 *  logged-in activity never leaks into the analytics tab. */
function VisitTracker() {
  const loc = useLocation();
  React.useEffect(() => {
    const path = loc.pathname || "/";
    const PRIVATE_PREFIXES = ["/doctor", "/admin", "/paciente", "/socio", "/embed"];
    if (PRIVATE_PREFIXES.some(p => path === p || path.startsWith(p + "/"))) return;
    const sp = new URLSearchParams(loc.search || "");
    const payload = {
      page: path,
      referrer: document.referrer || "",
      utm_source: sp.get("utm_source") || "",
      ref: sp.get("ref") || "",
    };
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/visits`;
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    } catch {}
    fetch(url, { method: "POST", keepalive: true,
                 headers: { "Content-Type": "application/json" },
                 body: JSON.stringify(payload) }).catch(() => {});
  }, [loc.pathname, loc.search]);
  return null;
}

function AppShell() {
  const isEmbed = typeof window !== "undefined" && window.location.pathname.startsWith("/embed/");
  return (
    <div className="App min-h-screen bg-slate-50">
      {!isEmbed && <Header />}
      <VisitTracker />
      <MetaPixel />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/sobre-nosotros" element={<AboutUs />} />
          <Route path="/para-medicos" element={<ParaMedicosLanding />} />
          <Route path="/para-medicos/:city" element={<ParaMedicosCity />} />
          <Route path="/medicos/:city" element={<MedicosCity />} />
          <Route path="/medicos/:city/:specialty" element={<MedicosCity />} />
          <Route path=":profileSlug" element={<DoctorProfile />} />
          <Route path="/buscar" element={<Search />} />
          <Route path="/doctor/:id" element={<DoctorProfile />} />
          <Route path="/doctor/login" element={<><Seo title="Ingresar médico" noindex/><DoctorAuth mode="login" /></>} />
          <Route path="/doctor/registro" element={<><Seo title="Registro médico" noindex/><DoctorAuth mode="register" /></>} />
          <Route path="/doctor/reset" element={<><Seo noindex/><DoctorReset /></>} />
          <Route path="/doctor/panel" element={<><Seo noindex/><RequireRole role="doctor"><DoctorDashboard /></RequireRole></>} />
          <Route path="/doctor/panel/:tab" element={<><Seo noindex/><RequireRole role="doctor"><DoctorDashboard /></RequireRole></>} />
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<><Seo noindex/><AdminLogin /></>} />
          <Route path="/admin/panel" element={<><Seo noindex/><RequireRole role="admin"><AdminDashboard /></RequireRole></>} />
          <Route path="/admin/panel/:section" element={<><Seo noindex/><RequireRole role="admin"><AdminDashboard /></RequireRole></>} />
          <Route path="/payment/success" element={<><Seo noindex/><PaymentResult status="success" /></>} />
          <Route path="/payment/cancel" element={<><Seo noindex/><PaymentResult status="cancel" /></>} />
          <Route path="/cita/gestionar" element={<><Seo title="Gestionar mi cita" noindex/><ManageBooking /></>} />
          <Route path="/legal/terminos" element={<><Seo title="Términos y Condiciones"/><Terms /></>} />
          <Route path="/legal/privacidad" element={<><Seo title="Política de Privacidad"/><Privacy /></>} />
          <Route path="/paciente/login" element={<><Seo noindex/><PatientLogin /></>} />
          <Route path="/paciente/verificar" element={<><Seo noindex/><PatientVerify /></>} />
          <Route path="/paciente/panel" element={<><Seo noindex/><RequireRole role="patient"><PatientDashboard /></RequireRole></>} />
          <Route path="/resena" element={<><Seo noindex/><ReviewPage /></>} />
          <Route path="/confirmar-cita" element={<><Seo noindex/><ConfirmBooking /></>} />
          <Route path="/embed/:id" element={<EmbedBooking />} />
          <Route path="/socio/registro" element={<><Seo title="Conviértase en Socio referidor" description="$9 USD al mes por cada médico referido que paga su suscripción (después de su mes gratis), transferidos a su cuenta bancaria en Ecuador."/><PartnerRegister /></>} />
          <Route path="/socio/login" element={<><Seo noindex/><PartnerLogin /></>} />
          <Route path="/socio/reset" element={<><Seo noindex/><PartnerReset /></>} />
          <Route path="/socio/activar" element={<><Seo noindex/><PartnerLogin /></>} />
          <Route path="/socio/panel" element={<><Seo noindex/><RequireRole role="partner"><PartnerDashboard /></RequireRole></>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isEmbed && <Footer />}
      {!isEmbed && <PwaBanner />}
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </AppProvider>
    </HelmetProvider>
  );
}
